import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { ReportPeriodInterface } from '@isl-shared/interfaces/app.interface'
import { DatePicker } from 'primeng/datepicker'

export const ISO8601Format = (value: Date) => {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export const dateFormat = (value: Date) => {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    return `${day}.${month}.${year}`
}

export const dateIsTheSame = (date1: Date, date2: Date) => {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    )
}

export const daysBetweenDates = (date1: Date, date2: Date) => {
    const firstDate = new Date(Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate()))
    const secondDate = new Date(Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate()))

    if (isNaN(firstDate.getTime()) || isNaN(secondDate.getTime())) {
        throw new Error('Invalid date format')
    }

    const differenceInMilliseconds = Math.abs(secondDate.getTime() - firstDate.getTime())
    return Math.ceil(differenceInMilliseconds / (1000 * 60 * 60 * 24))
}

export const isDateBigger = (date1: string, date2: string): boolean => {
    const firstDate = new Date(date1)
    const secondDate = new Date(date2)

    if (isNaN(firstDate.getTime()) || isNaN(secondDate.getTime())) {
        throw new Error('Invalid date format')
    }

    return firstDate > secondDate
}

export const isValidDate = (value: unknown): boolean => {
    return value instanceof Date && !isNaN(value.getTime())
}

export function dateRangeValidator(
    input1: string,
    input2: string,
    inputError: string,
    maxDifference: number
): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value1 = control.get(input1)?.value
        const value2 = control.get(input2)?.value

        if (!value1 || !value2) {
            return null
        }

        const date1 = new Date(value1)
        const date2 = new Date(value2)
        const controlWithError = control.get(inputError)

        const error: ValidationErrors | null = null

        controlWithError?.setErrors(null)
        if (isDateBigger(value1, value2)) {
            controlWithError?.setErrors({ bigger: true })
            return { dateRangeInvalid: true }
        }

        const days = daysBetweenDates(date1, date2)

        if (days > maxDifference) {
            controlWithError?.setErrors({ more100: true })
            return { dateRangeInvalid: true }
        }

        return error
    }
}

export const publicHolidayInputParams = () => {
    const lowerDateRange = new Date()
    lowerDateRange.setMonth(lowerDateRange.getMonth() - 2)
    const upperDateRange = new Date()
    upperDateRange.setMonth(upperDateRange.getMonth() + 2)
    return {
        to: ISO8601Format(upperDateRange),
        from: ISO8601Format(lowerDateRange)
    }
}

export const getTimeDifference = (startTime: string, endTime: string): string => {
    if (!startTime || !endTime || (startTime && !startTime.includes(':')) || (endTime && !endTime.includes(':')))
        return ''

    const [startHours, startMinutes] = startTime.split(':').map(Number)
    const [endHours, endMinutes] = endTime.split(':').map(Number)
    const startDate = new Date(0, 0, 0, startHours, startMinutes)
    const endDate = new Date(0, 0, 0, endHours, endMinutes)
    const diffInMs = endDate.getTime() - startDate.getTime()
    const diffInMinutes = (diffInMs < 0 ? diffInMs + 24 * 60 * 60 * 1000 : diffInMs) / (1000 * 60)
    const hours = Math.floor(diffInMinutes / 60)
    const minutes = diffInMinutes % 60

    const hoursString = hours < 10 ? `0${hours}` : `${hours}`
    const minutesString = minutes < 10 ? `0${minutes}` : `${minutes}`

    return `${hoursString}:${minutesString}`
}

export const findMatchingPeriod = (startDate: Date, periods: ReportPeriodInterface[]): ReportPeriodInterface => {
    const targetDate = new Date(startDate)
    targetDate.setHours(0, 0, 0, 0)

    for (const period of periods) {
        const periodStart = new Date(period.startDate + 'T00:00:00')
        const periodEnd = new Date(period.endDate + 'T23:59:59')

        if (targetDate >= periodStart && targetDate <= periodEnd) {
            return period
        }
    }

    return {
        startDate: '',
        endDate: ''
    }
}

export const getDatesInRange = (startDate: Date, endDate: Date): Date[] => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const dateArray = []

    while (start <= end) {
        dateArray.push(new Date(start))
        start.setDate(start.getDate() + 1)
    }

    return dateArray
}

export const countPeriodsByYear = (periods: ReportPeriodInterface[], year: number): number => {
    return periods.reduce((count, { startDate, endDate }) => {
        const startYear = new Date(startDate).getFullYear()
        const endYear = new Date(endDate).getFullYear()

        return count + (startYear <= year && endYear >= year ? 1 : 0)
    }, 0)
}

export const onDateInput = (event: Event): void => {
    const newValue = (event.target as HTMLInputElement).value
    const input = event.target as HTMLInputElement

    if (
        (newValue.length === 2 || newValue.length === 5) &&
        (event as InputEvent).inputType !== 'deleteContentBackward'
    ) {
        input.value = newValue + '.'
    }

    if (newValue.length > 10 && (event as InputEvent).inputType !== 'deleteContentBackward') {
        input.value = newValue.slice(0, 10)
    }

    if (newValue.length > 0 && !/^[0-9.]*$/.test(newValue)) {
        input.value = newValue.slice(0, -1)
    }
}

export const selectInputText = (datePicker: DatePicker): void => {
    setTimeout(() => {
        const inputElement = datePicker.el.nativeElement.querySelector('input')
        if (inputElement) {
            inputElement.select()
        }
    }, 0)
}

export const preventInvalidKeys = (event: KeyboardEvent): void => {
    const invalidChars = ['.', ',']
    if (invalidChars.includes(event.key)) {
        event.preventDefault()
    }
}

export const cleanTime = (value: string | null | undefined): string | null => {
    return value === '__:__' ? null : (value as string)
}
